import { Button, ClickAwayListener, createStyles, makeStyles, Popper } from '@material-ui/core';
import { Delete } from '@material-ui/icons';
import React, { useEffect, useRef, useState } from 'react';
import { useLocalization, useViewerState } from "../../hooks/index.js";
const useStyles = makeStyles(theme => createStyles({
  root: {
    position: 'absolute',
    resize: ({
      permissions,
      rotationDegree
    }) => permissions.canEdit && rotationDegree === 0 ? 'both' : 'none',
    overflow: 'auto',
    backgroundColor: 'black'
  },
  button: {
    backgroundColor: theme.palette.error.light
  }
}));

/**
 * Return a styled redaction component
 * @param shape Shape attributes
 * @param permissions Permissions of the current user
 * @param dimensions Shape dimensions
 * @param zoomRatio Zoom ratio
 * @param onDragStart Function triggered on drag event
 * @param onResized Function triggered on resized event
 * @param removeShape Function triggered on delete
 * @returns styled redaction component
 */
export function ShapeRedaction({
  shape,
  permissions,
  dimensions,
  zoomRatio,
  onDragStart,
  onResized,
  removeShape,
  rotationDegree
}) {
  const classes = useStyles({
    shape,
    permissions,
    dimensions,
    zoomRatio,
    onDragStart,
    onResized,
    removeShape,
    rotationDegree
  });
  const localization = useLocalization();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const id = open ? 'redaction-delete' : undefined;
  const redactionElement = useRef(null);
  const viewerState = useViewerState();
  const {
    updateState
  } = viewerState;
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (viewerState.currentlyResizedElementId === shape.guid) {
        var _redactionElement$cur;
        updateState({
          currentlyResizedElementId: undefined
        });
        const newSize = onResized((_redactionElement$cur = redactionElement.current) === null || _redactionElement$cur === void 0 ? void 0 : _redactionElement$cur.getClientRects()[0]);
        if (redactionElement.current && newSize) {
          redactionElement.current.style.width = `${newSize.w * zoomRatio}px`;
          redactionElement.current.style.height = `${newSize.h * zoomRatio}px`;
        }
      }
    };
    document.addEventListener('mouseup', handleGlobalMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [onResized, shape.guid, updateState, viewerState.currentlyResizedElementId, zoomRatio]);
  useEffect(() => {
    const mutationObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.attributeName === 'style') {
          updateState({
            currentlyResizedElementId: shape.guid
          });
        }
      });
    });
    redactionElement.current && mutationObserver.observe(redactionElement.current, {
      attributes: true
    });
    return () => mutationObserver.disconnect();
  }, [shape.guid, updateState]);
  const onRightClick = event => {
    event.preventDefault();
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: classes.root,
    tabIndex: 0,
    key: `r-${shape.h}-${shape.w}`,
    draggable: permissions.canEdit,
    onDragStart: onDragStart,
    onContextMenu: onRightClick,
    ref: redactionElement,
    style: {
      top: dimensions.top,
      left: dimensions.left,
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement(Popper, {
    id: id,
    open: open,
    anchorEl: anchorEl,
    placement: "right-start"
  }, /*#__PURE__*/React.createElement(ClickAwayListener, {
    onClickAway: () => setAnchorEl(null)
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    className: classes.button,
    onMouseUp: () => removeShape('redactions', shape.guid),
    startIcon: /*#__PURE__*/React.createElement(Delete, {
      scale: zoomRatio
    })
  }, localization.delete))));
}
//# sourceMappingURL=shape-redaction.js.map